<template>
  <div class="container">
    <p>{{  t('dateCalcDesc') }}</p>
    <hr />
    <div class="row">
      <div class="col-sm">
        <FreeInput
          :inputLabel="startDateLabel"
          v-model="startDateStr"
          :unit="isDateValid()"
        />
        <FreeInput
          :inputLabel="startTimeLabel"
          v-model="startTimeStr"
          :unit="isTimeValid()"
        />
        <div class="form-group">
          <input
            type="radio"
            id="addRadio"
            name="addSubRadioGroup"
            value="add"
            v-model="action"
          />
          <label for="addRadio">{{ addLabel }}</label>
          <br />
          <input
            type="radio"
            id="subRadio"
            name="addSubRadioGroup"
            value="sub"
            v-model="action"
          />
          <label for="subRadio">{{ subLabel }}</label>
        </div>
        <FreeInput
          :inputLabel="yearsLabel"
          v-model="years"
          type="number"
        />

        <FreeInput
          :inputLabel="monthsLabel"
          v-model="months"
          type="number" 
        />

        <FreeInput
          :inputLabel="daysLabel"
          v-model="days"
          type="number"
        />

        <FreeInput
          :inputLabel="hoursLabel"
          v-model="hours"
          type="number"
        />

        <FreeInput
          :inputLabel="minutesLabel"
          v-model="minutes"
          type="number" 
        />

        <FreeInput
          :inputLabel="secondsLabel"
          v-model="seconds"
          type="number"
        />
      
        <!-- ... similar for Months, Days, Hours, Minutes, Seconds -->
      </div>
      <div class="col-sm">
        <FreeOutput :outputLabel="adjustedDateLabel" :val="getFinalDate()" />
        <FreeOutput :outputLabel="adjustedDateLabel" :val="getFinalTime()" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed , reactive, watchEffect} from 'vue';
import dayjs from 'dayjs';
import FreeInput from '../inputs/FreeInput.vue'; // Adjust path as needed
import FreeOutput from '../inputs/FreeOutput.vue'; // Adjust path as needed
import { useI18n } from 'vue-i18n';

export default {
  name: 'DateCalculator',
  components: {
    FreeInput,
    FreeOutput
  },
  setup() {
    const startDateStr = ref(dayjs().format("YYYY-MM-DD"));
    const startTimeStr = ref(dayjs().format("HH:MM:ss"));
    const action = ref("add");
    const years = ref(0);
    const months = ref(0);
    const days = ref(0);
    const hours = ref(0);
    const minutes = ref(0);
    const seconds = ref(0);
    // ... similar for months, days, hours, minutes, seconds

    const { t } = useI18n();

    const startDateLabel = ref(t('StartDate'));
    const startTimeLabel = ref(t('StartTime'));
    const addLabel = ref(t('Add'));
    const subLabel = ref(t('Subtract'));
    const yearsLabel = ref(t('Years'));
    const monthsLabel = ref(t('Months'));
    const daysLabel = ref(t('Days'));
    const hoursLabel = ref(t('Hours'));
    const minutesLabel = ref(t('Minutes'));
    const secondsLabel = ref(t('Seconds'));
    const adjustedDateLabel = ref(t('AdjustedDate'));
    const adjustedTimeLabel = ref(t('AdjustedTime'));

    const invalidInput = ref(t('InvalidInput'));

    watchEffect(() => {
      startDateLabel.value = t('StartDate');
      startTimeLabel.value = t('StartTime');
      addLabel.value = t('Add');
      subLabel.value = t('Subtract');
      yearsLabel.value = t('Years');
      monthsLabel.value = t('Months');
      daysLabel.value = t('Days');
      hoursLabel.value = t('Hours');
      minutesLabel.value = t('Minutes');
      secondsLabel.value = t('Seconds');
      adjustedDateLabel.value = t('AdjustedDate');
      adjustedTimeLabel.value = t('AdjustedTime');
      invalidInput.value = t('InvalidInput');
    });

     // Create a reactive object with a function
     const translations = reactive({
        t: (key) => t(key)
      })


    const isDateValid = () => {
        // ... similar logic from React's isDateValid method
        const components = startDateStr.value.split("-");

        if (components.length !== 3) {
            return "❌";
        }

        if (components.some(component => isNaN(component))) {
            return "❌";
        }

        if (components[0].length != 4 || components[1].length != 2 || components[2].length != 2) {
            return "❌";
        }

        if (!dayjs(startDateStr.value).isValid()) {
            return "❌";
        }

        return "✅";

    };

    const isTimeValid = () => {
      // ... similar logic from React's isTimeValid method

       const components = startTimeStr.value.split(":");

        if (components.length !== 3) {
            return "❌";
        }

        if (components.some(component => isNaN(component) || component.length !== 2)) {
            return "❌";
        }

        if (
            Number(components[0]) >= 24 ||
            Number(components[1]) >= 60 ||
            Number(components[2]) >= 60
        ) {
            return "❌";
        }

        return "✅";
    };

    const getFinalDateISO = computed(() => {
      const original = dayjs(startDateStr.value + "T" + startTimeStr.value + "Z");
      let adjusted;

      if (action.value === "add") {
        adjusted = original.add(years.value, "year")
                      .add(months.value, "month")
                      .add(days.value, "day")
                      .add(hours.value, "hour")
                      .add(minutes.value, "minute")
                      .add(seconds.value, "second");
        // ... similar logic for adding months, days, etc.
      } else {
        adjusted = original.subtract(years.value, "year")
                      .subtract(months.value, "month")
                      .subtract(days.value, "day")
                      .subtract(hours.value, "hour")
                      .subtract(minutes.value, "minute")
                      .subtract(seconds.value, "second");
        // ... similar logic for subtracting months, days, etc.
      }

      return adjusted.toISOString();
    });

    const getFinalDate = () => {
      try {
        return getFinalDateISO.value.split("T")[0];
      } catch (e) {
        return invalidInput.value;
      }
    };

    const getFinalTime = () => {
      try {
        return getFinalDateISO.value.split("T")[1].substr(0, 8);
      } catch (e) {
        return invalidInput.value;
      }
    };

    return {
      t: translations.t,
      startDateLabel,
      startTimeLabel,
      addLabel,
      subLabel,
      yearsLabel,
      monthsLabel,
      daysLabel,
      hoursLabel,
      minutesLabel,
      secondsLabel,
      adjustedDateLabel,
      adjustedTimeLabel,
      invalidInput,
      startDateStr,
      startTimeStr,
      action,
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      // ... other states
      isDateValid,
      isTimeValid,
      getFinalDate,
      getFinalTime
    };
  }
};
</script>
